import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { FaKey, FaCopy, FaExclamationTriangle, FaTrashAlt, FaCalendarAlt } from 'react-icons/fa';
import { generateApiToken, fetchApiTokens, deleteApiToken } from '../services/apiTokenService';
import styles from '../style/pages/APITokenPage.module.css';

const APITokenPage = () => {
    const [tokenName, setTokenName] = useState("");
    const [expiration, setExpiration] = useState("30");
    const [loading, setLoading] = useState(false);
    const [generatedToken, setGeneratedToken] = useState(null);
    const [tokensList, setTokensList] = useState([]);

    useEffect(() => {
        loadTokens();
    }, []);

    const loadTokens = async () => {
        try {
            const data = await fetchApiTokens();
            setTokensList(data);
        } catch (error) {
            toast.error("Failed to load existing API tokens");
        }
    };

    const handleGenerate = async () => {
        if (!tokenName.trim()) {
            toast.warning("Please provide a name for this integration token.");
            return;
        }

        if (!window.confirm("Are you sure you want to generate a new API token? Ensure you keep it secure as it grants access to your HomeBox.")) {
            return;
        }

        setLoading(true);
        setGeneratedToken(null);
        try {
            const token = await generateApiToken(tokenName.trim(), parseInt(expiration, 10));
            setGeneratedToken(token);
            setTokenName("");
            toast.success("API Token Generated Successfully!");
            loadTokens();
        } catch (error) {
            toast.error(error.message || "Failed to generate token");
        } finally {
            setLoading(false);
        }
    };

    const handleRevoke = async (tokenId) => {
        if (!window.confirm("Are you sure you want to permanently revoke this token? Integrations using it will instantly fail.")) {
            return;
        }

        try {
            await deleteApiToken(tokenId);
            toast.success("Token revoked successfully");
            loadTokens();
        } catch (error) {
            toast.error("Failed to revoke token");
        }
    };

    const handleCopyToken = () => {
        if (generatedToken) {
            navigator.clipboard.writeText(generatedToken);
            toast.success("Token copied to clipboard!");
        }
    };

    return (
        <div className={styles.apiTokenContainer}>
            <div className={styles.header}>
                <h1>API Tokens</h1>
                <p>Generate long-lived authentication tokens for external applications and integrations.</p>
            </div>

            <div className={styles.contentLayout}>
                <div className={styles.glassCard}>
                <h3>Generate New Token</h3>
                <div className={styles.formStack}>
                    <div className={styles.formGroup}>
                        <label>Integration Name</label>
                        <input 
                            type="text" 
                            placeholder="e.g. Home Assistant, N8N Script"
                            value={tokenName} 
                            onChange={(e) => setTokenName(e.target.value)}
                            disabled={loading}
                            className={styles.textInput}
                        />
                    </div>
                    <div className={styles.formGroup}>
                        <label>Token Expiration</label>
                        <select 
                            value={expiration} 
                            onChange={(e) => setExpiration(e.target.value)}
                            disabled={loading}
                        >
                            <option value="7">7 Days</option>
                            <option value="30">30 Days</option>
                            <option value="90">90 Days</option>
                            <option value="365">1 Year</option>
                            <option value="-1">Never Expire</option>
                        </select>
                    </div>
                </div>

                <button 
                    className={styles.generateBtn} 
                    onClick={handleGenerate} 
                    disabled={loading || !tokenName.trim()}
                >
                    <FaKey /> {loading ? "Generating..." : "Generate Token"}
                </button>

                {generatedToken && (
                    <div className={styles.resultCard}>
                        <div className={styles.resultTitle}>
                            <FaKey /> Token Generated
                        </div>
                        <div className={styles.tokenBox}>
                            {generatedToken}
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <div className={styles.warningText}>
                                <FaExclamationTriangle /> Please copy this token now. You won't be able to see it again!
                            </div>
                            <button className={styles.generateBtn} onClick={handleCopyToken}>
                                <FaCopy /> Copy
                            </button>
                        </div>
                    </div>
                )}
            </div>

            <div className={`${styles.glassCard} ${styles.tableCard}`}>
                <h3>Active Tokens</h3>
                {tokensList.length === 0 ? (
                    <div className={styles.emptyState}>No active API tokens found.</div>
                ) : (
                    <div className={styles.tableWrapper}>
                        <table className={styles.tokenTable}>
                            <thead>
                                <tr>
                                    <th>Token Name</th>
                                    <th>Created At</th>
                                    <th>Expires At</th>
                                    <th>Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                {tokensList.map(t => (
                                    <tr key={t.id}>
                                        <td><strong>{t.tokenName}</strong></td>
                                        <td>{new Date(t.createdAt).toLocaleDateString()}</td>
                                        <td>
                                            <span className={styles.expiresBadge}>
                                                <FaCalendarAlt /> {new Date(t.expiresAt).toLocaleDateString()}
                                            </span>
                                        </td>
                                        <td>
                                            <button 
                                                className={styles.revokeBtn}
                                                onClick={() => handleRevoke(t.id)}
                                            >
                                                <FaTrashAlt /> Revoke
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
            </div>
        </div>
    );
};

export default APITokenPage;
